import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Copy, Pencil, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  deleteCampaignTemplateAPI,
  duplicateCampaignTemplateAPI,
  updateCampaignTemplateAPI,
} from '~features/email-builder/api/email-builder-api';
import type {
  EmailCampaignTemplate,
  EmailCampaignTemplateCategory,
} from '~features/email-builder/model/types';

import { QUERY_KEYS } from '~shared/api/query-keys';
import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';

const PREVIEW_LIMIT = 5;

const CATEGORIES: Array<EmailCampaignTemplateCategory> = [
  'recital',
  'camp',
  'weather',
  'general',
  'custom',
];

interface StudioTemplatesPanelProps {
  studioId: string | null | undefined;
  templates: Array<EmailCampaignTemplate>;
  activeTemplateId: string | null;
  onLoad: (template: EmailCampaignTemplate) => void;
  onDeleted?: (templateId: string) => void;
  onUpdated?: (template: EmailCampaignTemplate) => void;
  onDuplicated?: (template: EmailCampaignTemplate) => void;
}

export const StudioTemplatesPanel = ({
  studioId,
  templates,
  activeTemplateId,
  onLoad,
  onDeleted,
  onUpdated,
  onDuplicated,
}: StudioTemplatesPanelProps) => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [editTarget, setEditTarget] = useState<EmailCampaignTemplate | null>(
    null,
  );
  const [editName, setEditName] = useState('');
  const [editCategory, setEditCategory] =
    useState<EmailCampaignTemplateCategory>('custom');
  const [deleteTarget, setDeleteTarget] =
    useState<EmailCampaignTemplate | null>(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) {
      return templates;
    }
    return templates.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q),
    );
  }, [templates, search]);

  const visible = showAll ? filtered : filtered.slice(0, PREVIEW_LIMIT);
  const hiddenCount = Math.max(0, filtered.length - PREVIEW_LIMIT);

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.EMAIL_CAMPAIGN_TEMPLATES(studioId),
    });
  };

  const updateMutation = useMutation({
    mutationFn: () => {
      if (!editTarget) {
        return Promise.reject(new Error('No campaign selected'));
      }
      return updateCampaignTemplateAPI(editTarget._id, {
        name: editName.trim() || 'Untitled campaign',
        category: editCategory,
      });
    },
    onSuccess: (updated) => {
      invalidate();
      setEditTarget(null);
      onUpdated?.(updated);
      toast.success(`Updated “${updated.name}”`);
    },
    onError: (err: Error) => toast.error(err.message || 'Update failed'),
  });

  const duplicateMutation = useMutation({
    mutationFn: (templateId: string) =>
      duplicateCampaignTemplateAPI(templateId),
    onSuccess: (duplicated) => {
      invalidate();
      onDuplicated?.(duplicated);
      toast.success(`Duplicated as “${duplicated.name}”`);
    },
    onError: (err: Error) => toast.error(err.message || 'Duplicate failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!deleteTarget) {
        return Promise.reject(new Error('No campaign selected'));
      }
      return deleteCampaignTemplateAPI(deleteTarget._id).then(() => {
        return deleteTarget._id;
      });
    },
    onSuccess: (deletedId) => {
      invalidate();
      setDeleteTarget(null);
      onDeleted?.(deletedId);
      toast.success('Campaign deleted');
    },
    onError: (err: Error) => toast.error(err.message || 'Delete failed'),
  });

  return (
    <div className='space-y-2'>
      <Label>Campaigns</Label>
      <Input
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setShowAll(false);
        }}
        placeholder='Search campaigns…'
      />

      <div className='space-y-1'>
        {visible.map((template) => {
          const isActive = activeTemplateId === template._id;
          const audienceCount =
            (template.audience?.tags?.length ?? 0) +
            (template.audience?.classes?.length ?? 0) +
            (template.audience?.events?.length ?? 0);
          return (
            <div
              key={template._id}
              className={`flex items-start gap-1 rounded-md border px-2 py-1.5 ${
                isActive
                  ? 'border-primary bg-muted'
                  : 'border-border hover:bg-muted/60'
              }`}
            >
              <button
                type='button'
                className='min-w-0 flex-1 py-0.5 text-left text-sm'
                onClick={() => {
                  onLoad(template);
                }}
              >
                <div className='truncate font-medium'>{template.name}</div>
                <div className='text-xs capitalize text-muted-foreground'>
                  {template.category}
                  {audienceCount > 0
                    ? ` · ${audienceCount} tags/classes/events`
                    : ' · no audience yet'}
                  {isActive ? ' · active' : ''}
                </div>
              </button>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='size-8 shrink-0'
                title='Duplicate campaign'
                disabled={duplicateMutation.isPending}
                onClick={() => {
                  duplicateMutation.mutate(template._id);
                }}
              >
                <Copy className='size-3.5' />
              </Button>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='size-8 shrink-0'
                title='Edit name / category'
                onClick={() => {
                  setEditTarget(template);
                  setEditName(template.name);
                  setEditCategory(template.category);
                }}
              >
                <Pencil className='size-3.5' />
              </Button>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='size-8 shrink-0 text-destructive hover:text-destructive'
                title='Delete campaign'
                onClick={() => {
                  setDeleteTarget(template);
                }}
              >
                <Trash2 className='size-3.5' />
              </Button>
            </div>
          );
        })}

        {!filtered.length ? (
          <p className='text-sm text-muted-foreground'>
            {templates.length
              ? 'No campaigns match your search.'
              : 'No saved campaigns yet.'}
          </p>
        ) : null}
      </div>

      {filtered.length > PREVIEW_LIMIT ? (
        <Button
          type='button'
          variant='ghost'
          size='sm'
          className='h-8 px-2 text-xs'
          onClick={() => {
            setShowAll((v) => !v);
          }}
        >
          {showAll ? 'Show less' : `Show all (${hiddenCount} more)`}
        </Button>
      ) : null}

      <Dialog
        open={Boolean(editTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setEditTarget(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit campaign</DialogTitle>
          </DialogHeader>
          <div className='space-y-3'>
            <Input
              value={editName}
              onChange={(e) => {
                setEditName(e.target.value);
              }}
              placeholder='Campaign name'
            />
            <Select
              value={editCategory}
              onValueChange={(v) => {
                setEditCategory(v as EmailCampaignTemplateCategory);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder='Category' />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                setEditTarget(null);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                updateMutation.mutate();
              }}
              disabled={updateMutation.isPending}
            >
              Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete campaign?</DialogTitle>
          </DialogHeader>
          <p className='text-sm text-muted-foreground'>
            This removes “{deleteTarget?.name}” from your studio library. It
            does not affect Unlayer Console.
          </p>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                setDeleteTarget(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={() => {
                deleteMutation.mutate();
              }}
              disabled={deleteMutation.isPending}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
